import { useState } from "react";
import { getPortalSupabase } from "@/lib/portalSupabase";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";

export default function PortalLogin() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  // Preserve any returnTo param so the magic link can navigate there after auth
  const returnTo = new URLSearchParams(window.location.search).get("returnTo") ?? "";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setDevLink(null);

    try {
      // Route through server so email is sent via Resend (not Supabase's email provider)
      const res = await fetch("/api/portal/auth/request-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), returnTo: returnTo || undefined }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).message || `Server error ${res.status}`);
      }
      setSent(true);

      // In dev mode, also show a direct-link fallback
      if (import.meta.env.DEV) {
        try {
          const devRes = await fetch(`/api/portal/auth/dev-link?email=${encodeURIComponent(email.trim())}`);
          if (devRes.ok) {
            const { url } = await devRes.json();
            setDevLink(url);
          }
        } catch {
          // ignore
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to send sign-in link. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="flex flex-col items-center gap-2 mb-2">
            <img
              src="/carpenter-company-logo.png"
              alt="Carpenter &amp; Company, CPAs"
              className="h-16 w-auto"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="font-semibold text-lg">Carpenter &amp; Company, CPAs</span>
          </div>
          <p className="text-muted-foreground text-sm">Client Portal</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Sign in</CardTitle>
            <CardDescription>
              Enter your email to receive a secure sign-in link.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
                <p className="font-medium">Check your email</p>
                <p className="text-sm text-muted-foreground">
                  We sent a sign-in link to <strong>{email}</strong>. Click the link in the email to access your portal.
                </p>
                <Button variant="ghost" size="sm" onClick={() => setSent(false)}>
                  Use a different email
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                  />
                </div>
                {error && (
                  <div className="space-y-2">
                    <p className="text-sm text-destructive">{error}</p>
                    {devLink && (
                      <a
                        href={devLink}
                        className="block text-sm text-primary underline underline-offset-2"
                      >
                        Dev mode: click here to sign in directly →
                      </a>
                    )}
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={loading || !email.trim()}>
                  {loading ? "Sending..." : "Send sign-in link"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
